import { Autocomplete, Box, Chip, CircularProgress, Divider, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { useDebounce, useToast } from "../../../shared/hooks";
import { ProposalService } from "../../../shared/services/api/proposal/ProposalService";
import { colorBackGroundProposal, globalColorTextProposal, globalMaskDocument, translateProposal } from "../../../shared/utils";

interface IAutoCompleteProposalOption {
	proposal_certificate_number: number;
	proposal_id: string;
	proposal_clientId: string;
	proposal_name: string;
	proposal_document: string;
	proposal_status: string;
}

interface IAutoCompleteProposalProps {
	clientId?: string;
	labelSelect?: string;
	placeholder?: string;
	namecampo: string;
	onChange: (value: IAutoCompleteProposalOption | null) => void;
}

export const AutoCompleteProposal: React.FC<IAutoCompleteProposalProps> = ({ clientId, labelSelect = "Proposta", placeholder, namecampo, onChange }) => {
	const toastHandler = useToast();
	const { debounce } = useDebounce();

	const [busca, setBusca] = useState("");
	const [proposals, setProposals] = useState<IAutoCompleteProposalOption[]>([]);
	const [selectedProposal, setSelectedProposal] = useState<IAutoCompleteProposalOption | null>(null);
	const [isLoadingProposals, setIsLoadingProposals] = useState(false);

	useEffect(() => {
		setIsLoadingProposals(true);
		debounce(() => {
			if (!clientId) {
				setProposals([]);
				setIsLoadingProposals(false);
				return;
			}

			ProposalService.getIntallmentsComissionByClientId(clientId, 20, busca, "todos")
				.then((result) => {
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={"Erro"} content={"Erro ao buscar garantias"} />,
						});
						setProposals([]);
					} else {
						setProposals(result);
					}
				})
				.finally(() => {
					setIsLoadingProposals(false);
				});
		});
	}, [busca, clientId, debounce]);

	// Label formatada
	const getOptionLabel = (option: IAutoCompleteProposalOption) => {
		const numeroProposta = option.proposal_certificate_number ? String(option.proposal_certificate_number) : "Sem número";
		const nome = option.proposal_name ?? "";
		const status = option.proposal_status ? `(${translateProposal(option.proposal_status)})` : "";

		return `${numeroProposta} - ${nome} ${status}`;
	};

	return (
		<Autocomplete
			openText="Abrir"
			closeText="Fechar"
			clearText="Limpar"
			noOptionsText="Nenhuma opção encontrada"
			loadingText="Carregando..."
			loading={isLoadingProposals}
			options={proposals}
			value={selectedProposal}
			getOptionLabel={getOptionLabel}
			size="small"
			renderOption={(props, option) => (
				<li {...props}>
					<Box display="flex" flexDirection="column" width="100%" p={1}>
						{/* Linha principal */}
						<Tooltip title={option.proposal_name ?? ""} arrow placement="top-start">
							<Typography variant="body1" fontWeight="500" noWrap>
								{option.proposal_name ?? "-"} - ({globalMaskDocument(option.proposal_document)})
							</Typography>
						</Tooltip>

						{/* Linha secundária */}
						<Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" mt={0.5}>
							<Typography variant="body2" color="textSecondary">
								Certificado:{" "}
								<Typography component="span" variant="caption" fontWeight="bold">
									{option.proposal_certificate_number ?? "-"}
								</Typography>
							</Typography>
							<Chip
								label={translateProposal(option.proposal_status)}
								size="small"
								variant="filled"
								sx={{
									backgroundColor: colorBackGroundProposal(option.proposal_status),
									color: globalColorTextProposal(option.proposal_status),
									fontWeight: "normal",
								}}
							/>
						</Box>
						<Divider sx={{ mt: 0.5 }} />
					</Box>
				</li>
			)}
			isOptionEqualToValue={(option, value) => option.proposal_id === value.proposal_id}
			onChange={(_, newValue) => {
				setSelectedProposal(newValue);
				onChange(newValue);
			}}
			onInputChange={(_, newInputValue) => {
				setBusca(newInputValue);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={labelSelect}
					placeholder={placeholder}
					variant="outlined"
					fullWidth
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoadingProposals ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
